import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/common/components/ui/button";
import { Users, ChevronRight, Plus } from "lucide-react";
import { message } from "antd";
import { api } from "@/lib/api";
import { usePermissions } from "@/hooks/usePermissions";
import { ManageAccessModal } from "./components/ManageAccessModal";
import { CreateRoleModal } from "./components/CreateRoleModal";
import finEaseHomeNavigationRoutes from "@/projects/FinEaseHomeAndAdmin/utils/navigationRoutes";
import { RM } from "./components/theme";

export type RoleCardModel = {
  id: number;
  name: string;
  abbreviation: string;
  department: string;
  description: string;
  is_active: boolean;
};

const MOCK_ROLES: RoleCardModel[] = [
  {
    id: 1,
    name: "MMTC",
    abbreviation: "MMTC",
    department: "OPERATIONS",
    description: "Handles MMTC operational access and internal workflows.",
    is_active: true,
  },
  {
    id: 2,
    name: "Admin",
    abbreviation: "ADM",
    department: "ADMIN",
    description: "Manages day-to-day administration and role-based controls.",
    is_active: true,
  },
  {
    id: 3,
    name: "Super Admin",
    abbreviation: "SADM",
    department: "ADMIN",
    description: "Full platform access with complete permission management.",
    is_active: true,
  },
  {
    id: 4,
    name: "Legacy Analyst",
    abbreviation: "LA",
    department: "OPERATIONS",
    description: "Archived role — read-only",
    is_active: false,
  },
];

export default function RolesPage() {
  const [roles, setRoles] = useState<RoleCardModel[]>(MOCK_ROLES);
  const [loading, setLoading] = useState(false);
  const [accessOpen, setAccessOpen] = useState(false);
  const [createRoleOpen, setCreateRoleOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<RoleCardModel | null>(null);
  const { hasPermission } = usePermissions();

  const cloneRoleOptions = useMemo(
    () => roles.map(r => ({ id: r.id, name: r.name })),
    [roles]
  );

  const fetchRoles = async () => {
    try {
      const res = await api.rbac.roles.list();
      if (res.data && res.data.length > 0) {
        const mapped: RoleCardModel[] = (res.data as any[]).map((r, i) => ({
          id: r.id ?? i,
          name: r.name ?? "Role",
          abbreviation:
            r.abbreviation ??
            String(r.name ?? "R")
              .split(" ")
              .map((w: string) => w[0])
              .join("")
              .slice(0, 4)
              .toUpperCase(),
          department: r.department ?? "OPERATIONS",
          description: r.description ?? "",
          is_active: r.is_active !== false,
        }));
        setRoles(mapped);
      }
    } catch {
      // mock
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  const openManageAccess = (role: RoleCardModel) => {
    setSelectedRole(role);
    setAccessOpen(true);
  };

  const regionalHeadPreset = useMemo(() => {
    if (selectedRole?.name !== "Regional Head") {
      return undefined;
    }
    return {
      dashboard: { on: true, view: true, edit: true, delete: true },
      ipo: { on: true, view: true, edit: true, delete: true },
      ipo_service: { on: false, view: false, edit: false, delete: false },
      ipo_status: { on: false, view: false, edit: false, delete: false },
      ipo_allotment: { on: false, view: false, edit: false, delete: false },
    };
  }, [selectedRole]);

  return (
    <div className={RM.page}>
      <div className={RM.containerLoose}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className={RM.h1}>Role Management</h1>
            <p className={RM.subtitle}>Configure global permission sets.</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {hasPermission("admin-management:role-management:create") && (
              <Button
                className={RM.btnPrimary}
                onClick={() => setCreateRoleOpen(true)}
              >
                <Plus className="h-4 w-4" /> Create Role
              </Button>
            )}
          </div>
        </div>

        {loading && (
          <p className="text-sm text-muted-foreground">Loading roles…</p>
        )}

        <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
          {roles.map(role => (
            <div key={role.id} className={RM.cardGrid}>
              <div className="mb-4 flex items-start gap-3">
                <div className={RM.iconCircle}>
                  <Users className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className={RM.h2}>{role.name}</h2>
                    <span className={RM.abbrBadge}>{role.abbreviation}</span>
                  </div>
                  <p className={RM.deptLabel}>{role.department}</p>
                </div>
              </div>
              <p className="mb-6 flex-1 text-sm leading-relaxed text-muted-foreground">
                {role.description}
              </p>
              <button
                type="button"
                className="mt-auto flex items-center justify-end gap-1 self-end text-sm font-bold text-brandPrimary hover:text-brandDark hover:underline"
                onClick={() => openManageAccess(role)}
              >
                Manage Access
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>

        {roles.length === 0 && <p className={RM.emptyState}>No roles.</p>}

        <p className={RM.footnote}>
          Manage users in{" "}
          <Link
            className={RM.link}
            to={finEaseHomeNavigationRoutes.ROLE_MANAGEMENT_USERS}
          >
            Users
          </Link>
          .
        </p>
      </div>

      <ManageAccessModal
        open={accessOpen}
        onOpenChange={open => {
          setAccessOpen(open);
          if (!open) {
            setSelectedRole(null);
          }
        }}
        title={selectedRole?.name ?? "Role"}
        initialAccess={regionalHeadPreset}
        onSave={() => message.success("Role permissions saved (demo)")}
      />

      <CreateRoleModal
        open={createRoleOpen}
        onOpenChange={setCreateRoleOpen}
        cloneRoleOptions={cloneRoleOptions}
        onCreated={() => fetchRoles()}
      />
    </div>
  );
}
